import { memo, useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import {
  POSPLAY_HORIZONTAL_LINE_ZS,
  POSPLAY_ZONE14,
  POSPLAY_STROKE_HEX,
} from '../tactics2d/PositionalPlayGridLayer2D';

/**
 * 3D positional-play grid overlay. Two dashed yellow lines splitting
 * the pitch into thirds + the zone-14 highlight (centre channel of
 * the attacking third). Reuses the geometry constants from the 2D
 * sibling so both surfaces agree exactly.
 */

const FIELD_WIDTH_M = 68;
const HALF_W = FIELD_WIDTH_M / 2;
const LINE_Y = 0.011;
const ZONE14_Y = 0.009;

export interface PositionalPlayGridLayer3DProps {
  opacity: number;
}

function PositionalPlayGridLayer3DInner({
  opacity,
}: PositionalPlayGridLayer3DProps) {
  const horizontals = useMemo(
    () =>
      POSPLAY_HORIZONTAL_LINE_ZS.map((z, idx) => ({
        key: idx,
        points: [
          [-HALF_W, LINE_Y, z] as [number, number, number],
          [HALF_W, LINE_Y, z] as [number, number, number],
        ],
      })),
    [],
  );

  // Zone-14 outline + fill. Four world-corner points forming a
  // closed rectangle in the centre attacking third.
  const zone14Outline = useMemo<Array<[number, number, number]>>(() => {
    const { halfWidth, zMin, zMax } = POSPLAY_ZONE14;
    return [
      [-halfWidth, LINE_Y, zMin],
      [halfWidth, LINE_Y, zMin],
      [halfWidth, LINE_Y, zMax],
      [-halfWidth, LINE_Y, zMax],
      [-halfWidth, LINE_Y, zMin],
    ];
  }, []);

  // Filled translucent zone-14 rectangle — `THREE.PlaneGeometry` is
  // the simplest fit; rotate flat onto the pitch + position centred
  // on the attacking-centre channel.
  const zone14Geom = useMemo(() => {
    const { halfWidth, zMin, zMax } = POSPLAY_ZONE14;
    return new THREE.PlaneGeometry(halfWidth * 2, zMax - zMin);
  }, []);
  const zone14CentreZ =
    (POSPLAY_ZONE14.zMin + POSPLAY_ZONE14.zMax) / 2;

  return (
    <group raycast={() => undefined} renderOrder={3}>
      {/* Tinted zone-14 fill — sits just under the dividing lines so
          the outline reads on top. Y=0.009 vs LINE_Y=0.011. */}
      <mesh
        geometry={zone14Geom}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, ZONE14_Y, zone14CentreZ]}
      >
        <meshBasicMaterial
          color={POSPLAY_STROKE_HEX}
          transparent
          opacity={0.12 * opacity}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Two horizontal lines splitting the pitch into thirds. */}
      {horizontals.map(({ key, points }) => (
        <Line
          key={`third-${key}`}
          points={points}
          color={POSPLAY_STROKE_HEX}
          worldUnits
          lineWidth={0.18}
          dashed
          dashSize={1.4}
          gapSize={1.0}
          transparent
          opacity={opacity}
          depthWrite={false}
        />
      ))}
      {/* Zone-14 outline — slightly heavier dashed treatment than the
          third dividers so the danger zone stands off the grid. */}
      <Line
        points={zone14Outline}
        color={POSPLAY_STROKE_HEX}
        worldUnits
        lineWidth={0.22}
        dashed
        dashSize={2.0}
        gapSize={1.4}
        transparent
        opacity={Math.min(1, opacity + 0.2)}
        depthWrite={false}
      />
    </group>
  );
}

export const PositionalPlayGridLayer3D = memo(PositionalPlayGridLayer3DInner);
